import { type QueryResult, useLazyQuery } from "@apollo/client";
import { useInterval, useTimeout } from "@jidlomat/terminal-shared/hooks/timers";
import { getRandomNumberInRange } from "@jidlomat/terminal-shared/utils/math";
import { getMilliSecondsToNextHour } from "@jidlomat/terminal-shared/utils/time";
import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { getTerminalConfig } from "../config/terminalConfig";
import { selectQRCode, setQrCode } from "../slices/accessSlice";
import { setInitialDishTypes } from "../slices/dishesSlice";
import { setFridgeErrorType } from "../slices/fridgeInfoSlice";
import { selectIsLocalTransactionActive } from "../slices/transactionSlice";
import type { AppDispatch } from "../store";
import { ErrorType, type Fridge } from "../types";
import type { LocalFridgeState } from "../types/mainTypes";
import { setupNewFridgeId } from "../utils/browser/localStorage";
import { FETCH_DISH_TYPES } from "../utils/gqlOperations";
import { logToSentry } from "../utils/logger";
import { detectQrDrift } from "../utils/qrDrift";
import { reloadForRecoveryNow } from "../utils/recoveryReload";
import { deepOmit } from "../utils/utils";
import { useAppDispatch, useAppSelector } from "./utilsHooks/storeHooks";

interface Props {
	fridgeId: string;
}

export const handleSetDishTypes = (data: Fridge | LocalFridgeState, appDispatch: AppDispatch) => {
	const cleanedData: LocalFridgeState = deepOmit(data, ["__typename"]);
	appDispatch(setInitialDishTypes(cleanedData.dishes?.types));
	// Re-assert the access QR alongside prices. The hourly refetch defers while a local
	// transaction is active, so this can't overwrite the in-transaction nulled QR; guard on
	// truthy so a missing field never nulls a good QR.
	if (cleanedData.access?.qrCode?.code) {
		appDispatch(setQrCode(cleanedData.access.qrCode.code));
	}
};

// The end of an hourly refetch whose answer carries no fridge error: reload on a drifted QR, otherwise write the
// answer to Redux, or report a refetch that brought nothing.
const applyRefetchAnswer = async (
	response: QueryResult,
	localQrBeforeRefetch: string | null,
	fridgeId: string,
	appDispatch: AppDispatch,
) => {
	const beQrCode = response?.data?.fridge?.access?.qrCode?.code ?? null;
	// A drifted QR means a transaction happened that the subscription never delivered (zombie). The
	// subscription re-registers fine, so a restart wouldn't help; reload — the proven recovery. When the
	// storm guard suppresses the reload instead, fall through and heal the display with the fresh
	// snapshot, or the kiosk would keep showing the rotated-past (now invalid) QR until the next hour.
	if (
		(await detectQrDrift(beQrCode, localQrBeforeRefetch)) &&
		reloadForRecoveryNow(fridgeId, "qr-drift: missed transaction / zombie subscription")
	) {
		return;
	}

	if (response?.data) {
		// Data without an error means the fridge answered healthily again — clear the error state.
		// Deliberately NOT the unconditional mapping handleSetInitialData uses: the failure branch
		// below must leave the error state untouched, because a failed fetch proves nothing.
		appDispatch(setFridgeErrorType(null));
		handleSetDishTypes(response.data.fridge, appDispatch);
	} else {
		Sentry.captureException(new Error("Cannot refetch data"));
	}
};

// Exported for its test.
export const getInitialRefetchInterval = () => {
	const { mode, refetchNextHourOverride } = getTerminalConfig().build;
	const isProduction = mode === "production";

	if (isProduction) {
		return getMilliSecondsToNextHour();
	}
	if (refetchNextHourOverride === undefined) {
		return getMilliSecondsToNextHour();
	}
	return Number.parseInt(refetchNextHourOverride, 10);
};

export const useRefetchDishTypesQuery = ({ fridgeId }: Props) => {
	const appDispatch = useAppDispatch();
	// A boolean, not the transaction itself: re-renders only on the running/idle edge,
	// not on every subscription frame that lands a fresh payload in the slot.
	const isLocalTransactionActive = useAppSelector(selectIsLocalTransactionActive);
	// The on-screen (delivered) access QR. Mirrored in a ref so the refetch callback reads the latest value
	// without adding it to the callback deps; synced in an effect, not during render. The callback only ever
	// runs post-commit (timers / deferred-refetch effect), so the effect has always run by then.
	const localQrCode = useAppSelector(selectQRCode);
	const localQrCodeRef = useRef(localQrCode);
	useEffect(() => {
		localQrCodeRef.current = localQrCode;
	}, [localQrCode]);
	const [refetchInterval, setRefetchInterval] = useState<number | null>(getInitialRefetchInterval());
	const [refetchDebounceTimeout, setRefetchDebounceTimeout] = useState<number | null>(null);
	const [isDeferredRefetchPending, setIsDeferredRefetchPending] = useState(false);
	const { initialStateQueryRefetchDebounce } = getTerminalConfig().connectionSettings;
	const isMounted = useRef(true);

	// No onCompleted here: the ONLY Redux write for a refetch happens in handleRefetchDishTypes AFTER the
	// QR-drift check, so a drifted snapshot can never half-heal the display before we decide to reload.
	// no-cache (NOT network-only): this polling query selects only a slice of Fridge, and a cache write
	// would merge into the same normalized Fridge:<id> entity INITIAL_STATE_QUERY watches. On an errored
	// boot that merge flips `data.fridge.error` to null, opens the startup latch from a PARTIAL snapshot
	// (state/door/transaction/segments still hold the boot's nulls), and useRecoverFromFridgeError's
	// isSubscriptionSkipped guard then vetoes the full initial re-pull. Everything here reads
	// response.data directly; nothing needs this response in the cache.
	const [getDishTypes, { loading }] = useLazyQuery(FETCH_DISH_TYPES, {
		variables: { id: fridgeId },
		fetchPolicy: "no-cache",
	});

	// Symmetric arm/disarm: the body must undo everything the cleanup does. React StrictMode (dev
	// only — but that's every `pnpm dev` and Playwright session) runs mount→cleanup→remount at boot,
	// and a cleanup-only effect left isMounted=false with the interval nulled, permanently disarming
	// the hourly refetch in dev while production looked fine.
	useEffect(() => {
		isMounted.current = true;
		setRefetchInterval(getInitialRefetchInterval());
		return () => {
			isMounted.current = false;
			setRefetchInterval(null);
			setRefetchDebounceTimeout(null);
			setIsDeferredRefetchPending(false);
		};
	}, []);

	const handleRefetchDishTypes = useCallback(async () => {
		if (!isMounted.current) {
			return;
		}

		setIsDeferredRefetchPending(false);
		// Capture the on-screen (delivered) code BEFORE the fetch re-asserts it, so we can tell whether the
		// backend rotated past us while the subscription was silently not delivering.
		const localQrBeforeRefetch = localQrCodeRef.current;
		logToSentry(`Refetching prices: ${fridgeId}`, "info");

		const response = await getDishTypes();
		logToSentry(`Response from price refetch for fridge: ${fridgeId}`, "info");

		// EVERY errored snapshot carries null access/dishes — nothing to heal (treating it as healthy
		// would blank a working meal list), and its null QR must never enter the drift logic below;
		// record the error type and stop here.
		const fridgeError = response?.data?.fridge?.error;
		if (fridgeError?.type) {
			appDispatch(setFridgeErrorType(fridgeError.type));
			if (fridgeError.type === ErrorType.Unauthorized) {
				// Same handling as the initial query's UNAUTHORIZED path: the fridge id no longer
				// authorizes — re-prompt for a valid UUID.
				setupNewFridgeId(fridgeId, "Unauthorized access. Please provide a valid UUID");
			} else if (fridgeError.type !== ErrorType.Conflict) {
				logToSentry(`Error in price refetch for fridge: ${fridgeId}`, "warning", [fridgeError]);
			}
			return;
		}

		await applyRefetchAnswer(response, localQrBeforeRefetch, fridgeId, appDispatch);
	}, [fridgeId, getDishTypes, appDispatch]);

	// Resume a refetch that was deferred because a transaction was running. Owned here (not in
	// DeviceContext) so the provider doesn't need any transaction subscription at all.
	useEffect(() => {
		if (isDeferredRefetchPending && !isLocalTransactionActive) {
			handleRefetchDishTypes();
		}
	}, [isDeferredRefetchPending, isLocalTransactionActive, handleRefetchDishTypes]);

	// setup periodic refetch interval
	useInterval(() => {
		// Re-align to the next full hour on EVERY tick, not a fixed +1h: a WKWebView timer stall (suspension,
		// throttling) fires the pending tick late and setInterval then free-runs from the late fire, locking
		// the whole schedule off the hour permanently. Recomputing the boundary here pulls it back to :00 at
		// the next tick. (The value differs every tick, so useInterval re-arms against the wall clock.)
		setRefetchInterval(getMilliSecondsToNextHour());
		// add debounce to the request not to overload the server
		const debounce = getRandomNumberInRange(0, initialStateQueryRefetchDebounce);
		setRefetchDebounceTimeout(debounce);
	}, refetchInterval);

	// setup timeout for debounced refetch
	useTimeout(() => {
		if (!isMounted.current) {
			return;
		}
		// Reset after firing: if next hour's random draw equals the current value, the state wouldn't
		// change, useTimeout wouldn't re-arm, and that hour's refetch would silently skip.
		setRefetchDebounceTimeout(null);
		// Refetching mid-transaction would swap prices under the customer; defer until it ends.
		// (Covers local refills too — they change inventory just like orders.)
		if (isLocalTransactionActive) {
			setIsDeferredRefetchPending(true);
		} else {
			handleRefetchDishTypes();
		}
	}, refetchDebounceTimeout);

	return {
		loading: loading,
		handleRefetchDishTypes: handleRefetchDishTypes,
		isDeferredRefetchPending: isDeferredRefetchPending,
	};
};
